import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./lib/toast";
import { LanguageProvider } from "./lib/i18n";
import { BrandingProvider } from "./context/BrandingContext";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";
import { PageLoader } from "./lib/ui";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ControlTowerPage from "./pages/ControlTower";
import NightOps from "./pages/NightOps";
import PurchasingWorkspace from "./pages/PurchasingWorkspace";
import Traceability from "./pages/Traceability";
import WarehouseWorkspace from "./pages/WarehouseWorkspace";
import CentralNight from "./pages/CentralNight";
import CentralMorning from "./pages/CentralMorning";
import CentralProduction from "./pages/CentralProductionWorkspace";
import LossIncident from "./pages/LossIncident";
import MorningOps from "./pages/MorningOps";
import BranchDashboard from "./pages/BranchDashboard";
import BranchKitchen from "./pages/BranchKitchen";
import BranchInventory from "./pages/BranchInventory";
import BranchNotes from "./pages/BranchNotes";
import Recipes from "./pages/Recipes";
import CoffeeMachineCounter from "./pages/CoffeeMachineCounter";
import BeverageReceiving from "./pages/BeverageReceiving";
import BranchBeverageSettings from "./pages/BranchBeverageSettings";
import Sales from "./pages/Sales";
import Leakage from "./pages/Leakage";
import Orders from "./pages/Orders";
import OrderForm from "./pages/OrderForm";
import Purchasing from "./pages/Purchasing";
import Delivery from "./pages/Delivery";
import Receiving from "./pages/Receiving";
import Inventory from "./pages/Inventory";
import Ledger from "./pages/Ledger";
import Opname from "./pages/Opname";
import Approvals from "./pages/Approvals";
import Procurement from "./pages/Procurement";
import Expiry from "./pages/Expiry";
import Waste from "./pages/Waste";
import DailyClosing from "./pages/DailyClosing";
import Notifications from "./pages/Notifications";
import Audit from "./pages/Audit";
import MasterData from "./pages/MasterData";
import UsersPage from "./pages/Users";
import Feedback from "./pages/Feedback";
import CostLedger from "./pages/CostLedger";
import Reports from "./pages/Reports";
import InvoiceIntake from "./pages/InvoiceIntake";
import Production from "./pages/Production";
import MejaPrepare from "./pages/MejaPrepare";
import StockTransfer from "./pages/StockTransfer";
import PrepareDashboard from "./pages/PrepareDashboard";
import PrepareConfig from "./pages/PrepareConfig";
import MasterPersiapan from "./pages/MasterPersiapan";
import KitchenSOP from "./pages/KitchenSOP";
import KitchenOpsDashboard from "./pages/KitchenOpsDashboard";
import AIOpsAssistant from "./pages/AIOpsAssistant";
import MarginDashboard from "./pages/MarginDashboard";
import EmployeeMeal from "./pages/EmployeeMeal";
import AIAssistant from "./pages/AIAssistant";
import CentralFlowAI from "./pages/CentralFlowAI";
import ConfirmationAction from "./pages/ConfirmationAction";
import PriceChanges from "./pages/PriceChanges";
import PembelianCentral from "./pages/PembelianCentral";
import PembelianCabang from "./pages/PembelianCabang";
import RecipeCoverage from "./pages/RecipeCoverage";
import FinanceDashboard from "./pages/finance/FinanceDashboard";
import Reconciliation from "./pages/finance/Reconciliation";
import FinanceLiteDashboard from "./pages/finance-lite/FinanceLiteDashboard";
import FinanceLiteConfig from "./pages/finance-lite/FinanceLiteConfig";
import OperatingExpenses from "./pages/finance-lite/OperatingExpenses";
import MonthlyClosing from "./pages/finance-lite/MonthlyClosing";
import ProfitLoss from "./pages/finance/ProfitLoss";
import CashFlow from "./pages/finance/CashFlow";
import FinanceCategories from "./pages/finance/FinanceCategories";
import OutletAnalyzer from "./pages/finance/OutletAnalyzer";
import ScenarioSimulator from "./pages/finance/ScenarioSimulator";
import AICFO from "./pages/finance/AICFO";
import Accounting from "./pages/finance/Accounting";
import DataIntegrity from "./pages/DataIntegrity";
import BeginningBalance from "./pages/BeginningBalance";
import ERPHealth from "./pages/ERPHealth";
import BankImport from "./pages/finance/BankImport";

// Employee Portal (isolated) — /karyawan
import { EmployeeAuthProvider } from "./context/EmployeeAuthContext";
import EmployeeLogin from "./pages/employee/EmployeeLogin";
import EmployeeLayout from "./pages/employee/EmployeeLayout";
import EmpDashboard from "./pages/employee/EmpDashboard";
import EmpAttendance from "./pages/employee/EmpAttendance";
import EmpKPI from "./pages/employee/EmpKPI";
import EmpTraining from "./pages/employee/EmpTraining";
import EmpProfile from "./pages/employee/EmpProfile";
import EmpSchedule from "./pages/employee/EmpSchedule";
import EmpSop from "./pages/employee/EmpSop";
import EmpDeliveries from "./pages/employee/EmpDeliveries";
import EmpJobdesk from "./pages/employee/EmpJobdesk";
import EmpChecklist from "./pages/employee/EmpChecklist";
import EmpChangePin from "./pages/employee/EmpChangePin";
import EmpHub from "./pages/employee/EmpHub";
import EmpPayroll from "./pages/employee/EmpPayroll";
import EmpAnnouncements from "./pages/employee/EmpAnnouncements";

// HRD admin
import HRDashboard from "./pages/hr/HRDashboard";
import HREmployees from "./pages/hr/HREmployees";
import HRAttendance from "./pages/hr/HRAttendance";
import HRKpi from "./pages/hr/HRKpi";
import HRTraining from "./pages/hr/HRTraining";
import HRPayroll from "./pages/hr/HRPayroll";
import HRJobdesk from "./pages/hr/HRJobdesk";
import HRPositions from "./pages/hr/HRPositions";
import HRCompliance from "./pages/hr/HRCompliance";
import HRKpiTemplates from "./pages/hr/HRKpiTemplates";
import HRPayrollRules from "./pages/hr/HRPayrollRules";
import HRSchedule from "./pages/hr/HRSchedule";
import HRSop from "./pages/hr/HRSop";
import HRAnnouncements from "./pages/hr/HRAnnouncements";
import EmailSettings from "./pages/EmailSettings";
import SalesInput from "./pages/SalesInput";
import PettyCash from "./pages/PettyCash";
import SalesHistory from "./pages/SalesHistory";
import CashierIncident from "./pages/CashierIncident";

const FINANCE_ONLY_ROLES = ["FINANCE_STAFF", "FINANCE_MANAGER"];

function Protected({ children, perm }) {
  const { user, hasPermission, permsLoaded } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;
  // Wait until permissions are loaded before rendering a permission-gated page,
  // so an unauthorized page never mounts (and fires API calls) before redirecting.
  if (perm && !permsLoaded) return <Layout><PageLoader /></Layout>;
  // Finance-only roles are sandboxed to the Finance module (+ personal Notifications).
  // They are redirected away from every operational route UNLESS explicitly granted
  // that route's permission later (perm check below still applies).
  if (FINANCE_ONLY_ROLES.includes(user.role)) {
    const inFinanceSandbox = location.pathname.startsWith("/finance") || location.pathname === "/notifications";
    if (!inFinanceSandbox && (!perm || (permsLoaded && !hasPermission(perm)))) {
      return <Navigate to="/finance" replace />;
    }
  }
  if (perm && !hasPermission(perm)) return <Navigate to="/" replace />;
  return <Layout><ErrorBoundary key={location.pathname}>{children}</ErrorBoundary></Layout>;
}

function Home() {
  const { user, workspaceKey } = useAuth();
  if (FINANCE_ONLY_ROLES.includes(user?.role)) return <Navigate to="/finance" replace />;
  if (user?.role === "HR_MANAGER") return <Navigate to="/hr" replace />;
  if (user?.role === "CHECKER_CENTRAL") return <Navigate to="/central-morning" replace />;
  if (workspaceKey === "cashier") return <Navigate to="/sales-input" replace />;
  if (user?.role === "KITCHEN" && user?.kitchen_type === "branch") return <Navigate to="/kitchen-dashboard" replace />;
  if (user?.role === "KITCHEN") return <Navigate to="/kitchen-dashboard" replace />;
  if (user?.role === "BRANCH_STAFF" || user?.role === "BRANCH_MANAGER") return <BranchKitchen />;
  return <Dashboard />;
}

function App() {
  return (
    <LanguageProvider>
      <BrandingProvider>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><Home /></Protected>} />
            <Route path="/control-tower" element={<Protected perm="view_dashboard"><ControlTowerPage /></Protected>} />
            <Route path="/night-ops" element={<Protected perm="view_orders"><NightOps /></Protected>} />
            <Route path="/morning-ops" element={<Protected perm="view_orders"><MorningOps /></Protected>} />
            <Route path="/loss-incident" element={<Protected><LossIncident /></Protected>} />
            <Route path="/sales-input" element={<Protected perm="create_sales"><SalesInput /></Protected>} />
            <Route path="/sales-history" element={<Protected perm="view_sales"><SalesHistory /></Protected>} />
            <Route path="/petty-cash" element={<Protected perm="view_sales"><PettyCash /></Protected>} />
            <Route path="/sales-incident" element={<Protected perm="create_incident"><CashierIncident /></Protected>} />
            <Route path="/purchasing-workspace" element={<Protected perm="view_purchasing"><PurchasingWorkspace /></Protected>} />
            <Route path="/supply-chain" element={<Protected perm="view_purchasing"><PurchasingWorkspace /></Protected>} />
            <Route path="/traceability" element={<Protected perm="view_purchasing"><Traceability /></Protected>} />
            <Route path="/central-night" element={<Protected perm="view_central_ops"><CentralNight /></Protected>} />
            <Route path="/central-morning" element={<Protected perm="view_central_ops"><CentralMorning /></Protected>} />
            <Route path="/central-production" element={<Protected perm="view_production"><CentralProduction /></Protected>} />
            <Route path="/central-afternoon" element={<Protected perm="view_production"><CentralProduction /></Protected>} />
            <Route path="/branch-kitchen" element={<Protected perm="view_dashboard"><BranchKitchen /></Protected>} />
            <Route path="/warehouse-workspace" element={<Protected perm="view_purchasing"><WarehouseWorkspace /></Protected>} />
            <Route path="/orders" element={<Protected perm="view_orders"><Orders /></Protected>} />
            <Route path="/orders/new" element={<Protected perm="view_orders"><OrderForm /></Protected>} />
            <Route path="/orders/:id/edit" element={<Protected perm="view_orders"><OrderForm /></Protected>} />
            <Route path="/my-inventory" element={<Protected perm="view_inventory"><BranchInventory /></Protected>} />
            <Route path="/branch-notes" element={<Protected><BranchNotes /></Protected>} />
            <Route path="/recipes" element={<Protected><Recipes /></Protected>} />
            <Route path="/coffee-machine-counter" element={<Protected><CoffeeMachineCounter /></Protected>} />
            <Route path="/beverage-receiving" element={<Protected><BeverageReceiving /></Protected>} />
            <Route path="/beverage-settings" element={<Protected><BranchBeverageSettings /></Protected>} />
            <Route path="/sales" element={<Protected perm="view_sales"><Sales /></Protected>} />
            <Route path="/leakage" element={<Protected perm="view_leakage"><Leakage /></Protected>} />
            <Route path="/purchasing" element={<Protected perm="view_purchasing"><Purchasing /></Protected>} />
            <Route path="/delivery" element={<Protected perm="view_delivery"><Delivery /></Protected>} />
            <Route path="/receiving" element={<Protected perm="view_receiving"><Receiving /></Protected>} />
            <Route path="/inventory" element={<Protected perm="view_inventory"><Inventory /></Protected>} />
            <Route path="/ledger" element={<Protected perm="view_inventory"><Ledger /></Protected>} />
            <Route path="/cost-ledger" element={<Protected perm="view_purchasing"><CostLedger /></Protected>} />
            <Route path="/reports" element={<Protected perm="view_purchasing"><Reports /></Protected>} />
            <Route path="/invoice-intake" element={<Protected perm="view_purchasing"><InvoiceIntake /></Protected>} />
            <Route path="/production" element={<Protected perm="view_production"><Production /></Protected>} />
            <Route path="/meja-prepare" element={<Protected perm="view_prepare"><MejaPrepare /></Protected>} />
            <Route path="/meja-prepare/dashboard" element={<Protected perm="view_prepare_dashboard"><PrepareDashboard /></Protected>} />
            <Route path="/meja-prepare/config" element={<Protected perm="manage_prep_config"><PrepareConfig /></Protected>} />
            <Route path="/master-persiapan" element={<Protected perm="manage_prep_config"><MasterPersiapan /></Protected>} />
            <Route path="/kitchen-sop" element={<Protected perm="manage_prep_config"><KitchenSOP /></Protected>} />
            <Route path="/kitchen-dashboard" element={<Protected perm="view_prepare"><KitchenOpsDashboard /></Protected>} />
            <Route path="/ai-ops-assistant" element={<Protected><AIOpsAssistant /></Protected>} />
            <Route path="/central-flow-ai" element={<Protected><CentralFlowAI /></Protected>} />
            <Route path="/confirmation-action" element={<Protected><ConfirmationAction /></Protected>} />
            <Route path="/transfers" element={<Protected perm="view_inventory"><StockTransfer /></Protected>} />
            <Route path="/margin-dashboard" element={<Protected perm="view_margin_dashboard"><MarginDashboard /></Protected>} />
            <Route path="/ai-assistant" element={<Protected perm="view_ai_assistant"><AIAssistant /></Protected>} />
            <Route path="/price-changes" element={<Protected perm="view_price_changes"><PriceChanges /></Protected>} />
            <Route path="/pembelian-central" element={<Protected perm="view_purchase_central"><PembelianCentral /></Protected>} />
            <Route path="/pembelian-cabang" element={<Protected perm="view_purchase_branch"><PembelianCabang /></Protected>} />
            <Route path="/recipe-coverage" element={<Protected perm="view_recipe_coverage"><RecipeCoverage /></Protected>} />
            <Route path="/finance" element={<Protected perm="view_finance_dashboard"><FinanceDashboard /></Protected>} />
            <Route path="/finance/reconciliation" element={<Protected perm="view_finance_dashboard"><Reconciliation /></Protected>} />
            <Route path="/finance-lite" element={<Protected perm="view_finance_dashboard"><FinanceLiteDashboard /></Protected>} />
            <Route path="/finance-lite/config" element={<Protected perm="view_finance_dashboard"><FinanceLiteConfig /></Protected>} />
            <Route path="/finance-lite/expenses" element={<Protected perm="view_finance_dashboard"><OperatingExpenses /></Protected>} />
            <Route path="/finance-lite/closing" element={<Protected perm="view_finance_dashboard"><MonthlyClosing /></Protected>} />
            <Route path="/finance/profit-loss" element={<Protected perm="view_profit_loss"><ProfitLoss /></Protected>} />
            <Route path="/finance/cash-flow" element={<Protected perm="view_cash_flow"><CashFlow /></Protected>} />
            <Route path="/finance/categories" element={<Protected perm="view_finance_categories"><FinanceCategories /></Protected>} />
            <Route path="/finance/outlet-analyzer" element={<Protected perm="view_finance_advanced"><OutletAnalyzer /></Protected>} />
            <Route path="/finance/scenario-simulator" element={<Protected perm="view_finance_advanced"><ScenarioSimulator /></Protected>} />
            <Route path="/finance/ai-cfo" element={<Protected perm="view_ai_cfo"><AICFO /></Protected>} />
            <Route path="/finance/bank-import" element={<Protected perm="view_bank_import"><BankImport /></Protected>} />
            <Route path="/finance/accounting" element={<Protected perm="view_accounting"><Accounting /></Protected>} />
            <Route path="/data-integrity" element={<Protected perm="view_integrity"><DataIntegrity /></Protected>} />
            <Route path="/beginning-balance" element={<Protected perm="view_integrity"><BeginningBalance /></Protected>} />
            <Route path="/erp-health" element={<Protected perm="view_integrity"><ERPHealth /></Protected>} />
            <Route path="/opname" element={<Protected perm="view_stock_take"><Opname /></Protected>} />
            <Route path="/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/procurement" element={<Protected perm="view_purchasing"><Procurement /></Protected>} />
            <Route path="/expiry" element={<Protected><Expiry /></Protected>} />
            <Route path="/waste" element={<Protected perm="view_waste"><Waste /></Protected>} />
            <Route path="/employee-meals" element={<Protected perm="view_employee_meal"><EmployeeMeal /></Protected>} />
            <Route path="/closing" element={<Protected><DailyClosing /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/master-data" element={<Protected><MasterData /></Protected>} />
            <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
            <Route path="/users" element={<Protected perm="view_users"><UsersPage /></Protected>} />
            <Route path="/settings/email" element={<Protected perm="view_users"><EmailSettings /></Protected>} />
            {/* HRD admin */}
            <Route path="/hr" element={<Protected perm="view_hr_dashboard"><HRDashboard /></Protected>} />
            <Route path="/hr/employees" element={<Protected perm="view_employees"><HREmployees /></Protected>} />
            <Route path="/hr/attendance" element={<Protected perm="view_attendance"><HRAttendance /></Protected>} />
            <Route path="/hr/kpi" element={<Protected perm="view_kpi"><HRKpi /></Protected>} />
            <Route path="/hr/training" element={<Protected perm="view_training"><HRTraining /></Protected>} />
            <Route path="/hr/payroll" element={<Protected perm="view_payroll_preview"><HRPayroll /></Protected>} />
            <Route path="/hr/jobdesks" element={<Protected perm="view_employees"><HRJobdesk /></Protected>} />
            <Route path="/hr/positions" element={<Protected perm="view_employees"><HRPositions /></Protected>} />
            <Route path="/hr/compliance" element={<Protected perm="view_attendance"><HRCompliance /></Protected>} />
            <Route path="/hr/kpi-templates" element={<Protected perm="view_kpi"><HRKpiTemplates /></Protected>} />
            <Route path="/hr/payroll-rules" element={<Protected perm="view_payroll_preview"><HRPayrollRules /></Protected>} />
            <Route path="/hr/schedule" element={<Protected perm="view_attendance"><HRSchedule /></Protected>} />
            <Route path="/hr/announcements" element={<Protected perm="view_hr_dashboard"><HRAnnouncements /></Protected>} />
            <Route path="/hr/sop" element={<Protected perm="view_training"><HRSop /></Protected>} />
            {/* Employee Portal — isolated auth + layout (NOT admin) */}
            <Route path="/karyawan/login" element={<EmployeeAuthProvider><EmployeeLogin /></EmployeeAuthProvider>} />
            <Route path="/karyawan" element={<EmployeeAuthProvider><EmployeeLayout /></EmployeeAuthProvider>}>
              <Route index element={<EmpDashboard />} />
              <Route path="absensi" element={<EmpAttendance />} />
              <Route path="kpi" element={<EmpKPI />} />
              <Route path="training" element={<EmpTraining />} />
              <Route path="jadwal" element={<EmpSchedule />} />
              <Route path="sop" element={<EmpSop />} />
              <Route path="pengiriman" element={<EmpDeliveries />} />
              <Route path="jobdesk" element={<EmpJobdesk />} />
              <Route path="checklist" element={<EmpChecklist />} />
              <Route path="menu" element={<EmpHub />} />
              <Route path="payroll" element={<EmpPayroll />} />
              <Route path="pengumuman" element={<EmpAnnouncements />} />
              <Route path="ganti-pin" element={<EmpChangePin />} />
              <Route path="profil" element={<EmpProfile />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
      </BrandingProvider>
    </LanguageProvider>
  );
}

export default App;
